import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { HardHat } from "lucide-react";
import { US_STATES, SERVICE_CATEGORIES } from "@/lib/constants";
import { toast } from "sonner";

export default function RegisterContractor() {
  const { registerContractor } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ first_name: "", last_name: "", company_name: "", phone: "", email: "", password: "", trade: "", secondary_trades: "", city: "", state: "", zip_code: "", service_radius: "", years_experience: "", availability: "", emergency_availability: false, tools: "", transportation: "", crew_size: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await registerContractor(f);
      toast.success("Application submitted!");
      navigate("/app/contractor");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-6">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center"><HardHat className="w-5 h-5 text-white" /></div>
          <span className="font-head font-bold text-lg text-slate-900">SJ Property Care</span>
        </Link>
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 md:p-8">
          <h1 className="font-head text-2xl md:text-3xl font-semibold text-slate-900">Contractor / Vendor Application</h1>
          <p className="mt-2 text-slate-500 text-sm">Join our network of vetted trade professionals. Upload documents after signing in.</p>
          <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-4">
            <div><Label>First Name</Label><Input data-testid="con-first-name" required value={f.first_name} onChange={set("first_name")} className="mt-1.5" /></div>
            <div><Label>Last Name</Label><Input data-testid="con-last-name" required value={f.last_name} onChange={set("last_name")} className="mt-1.5" /></div>
            <div className="sm:col-span-2"><Label>Company Name</Label><Input data-testid="con-company" value={f.company_name} onChange={set("company_name")} className="mt-1.5" /></div>
            <div><Label>Phone</Label><Input data-testid="con-phone" required value={f.phone} onChange={set("phone")} className="mt-1.5" /></div>
            <div><Label>Email</Label><Input data-testid="con-email" type="email" required value={f.email} onChange={set("email")} className="mt-1.5" /></div>
            <div className="sm:col-span-2"><Label>Password</Label><PasswordInput data-testid="con-password" required value={f.password} onChange={set("password")} className="mt-1.5" /></div>
            <div>
              <Label>Primary Trade</Label>
              <Select value={f.trade} onValueChange={set("trade")}>
                <SelectTrigger data-testid="con-trade" className="mt-1.5"><SelectValue placeholder="Select trade" /></SelectTrigger>
                <SelectContent className="max-h-64">{SERVICE_CATEGORIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Secondary Trades</Label><Input data-testid="con-secondary" value={f.secondary_trades} onChange={set("secondary_trades")} className="mt-1.5" placeholder="Comma separated" /></div>
            <div><Label>City</Label><Input data-testid="con-city" required value={f.city} onChange={set("city")} className="mt-1.5" /></div>
            <div>
              <Label>State</Label>
              <Select value={f.state} onValueChange={set("state")}>
                <SelectTrigger data-testid="con-state" className="mt-1.5"><SelectValue placeholder="State" /></SelectTrigger>
                <SelectContent className="max-h-64">{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>ZIP Code</Label><Input data-testid="con-zip" value={f.zip_code} onChange={set("zip_code")} className="mt-1.5" /></div>
            <div><Label>Service Radius</Label><Input data-testid="con-radius" value={f.service_radius} onChange={set("service_radius")} className="mt-1.5" placeholder="e.g. 30 mi" /></div>
            <div><Label>Years Experience</Label><Input data-testid="con-experience" value={f.years_experience} onChange={set("years_experience")} className="mt-1.5" /></div>
            <div><Label>Availability</Label><Input data-testid="con-availability" value={f.availability} onChange={set("availability")} className="mt-1.5" placeholder="e.g. Weekdays" /></div>
            <div><Label>Crew Size</Label><Input data-testid="con-crew" value={f.crew_size} onChange={set("crew_size")} className="mt-1.5" /></div>
            <div><Label>Tools</Label><Input data-testid="con-tools" value={f.tools} onChange={set("tools")} className="mt-1.5" /></div>
            <div><Label>Transportation</Label><Input data-testid="con-transport" value={f.transportation} onChange={set("transportation")} className="mt-1.5" placeholder="e.g. Van" /></div>
            <div className="flex items-center justify-between sm:col-span-2 rounded-lg border border-slate-200 p-3">
              <Label className="mb-0">Emergency Availability</Label>
              <Switch data-testid="con-emergency" checked={f.emergency_availability} onCheckedChange={(v) => setF({ ...f, emergency_availability: v })} />
            </div>
            {error && <p className="sm:col-span-2 text-sm text-rose-600">{error}</p>}
            <div className="sm:col-span-2">
              <Button data-testid="con-submit" type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 h-11">{loading ? "Submitting..." : "Submit Application"}</Button>
            </div>
          </form>
          <p className="mt-4 text-sm text-slate-500">Already applied? <Link to="/login" className="text-blue-600 font-medium">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
}
